import { trackWebEvent } from "./web-analytics";
import { devError } from "./dev-log";

/**
 * Click autocapture for the first-party web pipeline. One listener on the document, registered in the
 * capture phase, sees every click (the capture phase runs on document for every click) and records
 * the nearest interactive element — mainly its stable `id` (see the id convention in CLAUDE.md), which
 * is the analytics key. No per-element wiring needed.
 *
 * Privacy: this must never vacuum up PII. We never read input values, and we drop element text that
 * looks like an email/phone or sits inside a [data-sensitive] region.
 */

// Native interactive tags and ARIA roles. React onClick is a synthetic handler with no DOM attribute,
// so it cannot be matched by a selector; instead we detect "clickable" the way PostHog's autocapture
// does — primarily via computed `cursor: pointer` (see onClick) — which catches onClick <div>s, cards
// and icons without touching React internals.
// `label` is deliberately excluded: it is not the actionable control, and clicks on a label bound to a
// control are dropped in onClick (the browser re-fires them on the control, which we capture there).
const INTERACTIVE_TAGS = new Set(["a", "button", "input", "select", "textarea", "video", "audio"]);
const INTERACTIVE_ROLES = new Set([
  "button",
  "link",
  "tab",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "option",
  "checkbox",
  "radio",
  "switch",
]);
const VALUE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
const EMAIL_OR_PHONE = /[\w.+-]+@[\w-]+\.\w+|\+?\d[\d\s().-]{6,}\d/;
const MAX_TEXT_LEN = 80;

let initialized = false;

function isInteractive(el: HTMLElement): boolean {
  if (INTERACTIVE_TAGS.has(el.tagName.toLowerCase())) return true;
  const role = el.getAttribute("role");
  return !!role && INTERACTIVE_ROLES.has(role);
}

// Scan a bounded prefix (not the whole subtree, which for a large clickable container can be
// thousands of chars) for PII. The prefix is wider than MAX_TEXT_LEN so a phone/email straddling the
// stored cutoff is still caught, while the regex never runs over unbounded text.
const PII_SCAN_LEN = 256;

/** Safe label for an element: never its typed value, never PII-looking text. */
function safeElementText(el: HTMLElement): string | null {
  if (VALUE_TAGS.has(el.tagName)) return null; // never capture what a user typed
  if (el.closest("[data-sensitive]")) return null;
  const sample = (el.textContent?.trim() ?? "").slice(0, PII_SCAN_LEN);
  if (!sample || EMAIL_OR_PHONE.test(sample)) return null;
  return sample.slice(0, MAX_TEXT_LEN);
}

function onClick(e: MouseEvent): void {
  // This is a capture-phase listener on document, so it runs on EVERY click. A throw here would
  // surface as an uncaught error on every click and flood Sentry; analytics is best-effort, so any
  // failure (a store read, getComputedStyle, storage access) is swallowed.
  try {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    // A <label> bound to an ENABLED control makes the browser re-fire the click on that control, which
    // we capture there (with the control's id) — so drop this one to avoid a double-count. Keep it in
    // every other case: a click that already landed on an interactive element (a real single click,
    // incl. the control itself), or a disabled/absent control (no synthetic click follows, so dropping
    // would silently lose the event rather than de-dupe it).
    const label = target.closest("label");
    if (label) {
      const control = (label as HTMLLabelElement).control as (HTMLElement & { disabled?: boolean }) | null;
      if (control && !control.disabled && !control.contains(target) && !isInteractive(target)) return;
    }

    // Attribute the click to the nearest native-interactive ancestor (tag / ARIA role) if there is one.
    let el: HTMLElement | null = target;
    while (el && el !== document.body && !isInteractive(el)) {
      el = el.parentElement;
    }
    const interactiveEl = el && el !== document.body ? el : null;

    let attributed: HTMLElement;
    if (interactiveEl) {
      attributed = interactiveEl;
    } else {
      // Not a native control: capture it only if it is styled clickable (cursor: pointer). Because
      // cursor inherits, a pointer on the clicked child means the real clickable is an ancestor
      // container (a card / tappable area). Walk up through the contiguous pointer region to the
      // innermost element carrying an id — recovering the container's stable id even though the user
      // clicked a child with none. Stop at the region boundary so we never borrow an id from an
      // unrelated ancestor outside the clickable area. getComputedStyle runs at user-click pace, so
      // the cost is negligible; a non-clickable target (plain text) breaks out on the first check.
      let node: HTMLElement | null = target;
      let idBearer: HTMLElement | null = null;
      let clickable = false;
      while (node && node !== document.body && window.getComputedStyle(node).cursor === "pointer") {
        clickable = true;
        if (node.id) {
          idBearer = node;
          break;
        }
        node = node.parentElement;
      }
      if (!clickable) return;
      attributed = idBearer ?? target;
    }

    trackWebEvent({
      event_type: "click",
      // The element's OWN id, or — for a styled-clickable container — the nearest id within the
      // clickable region. Never an id borrowed from outside that region. No id in range reports null
      // (+ tag/text); the fix is to add an id per the CLAUDE.md convention, not to guess from a wrapper.
      element_id: attributed.id || null,
      element_tag: attributed.tagName.toLowerCase(),
      element_role: attributed.getAttribute("role"),
      element_text: safeElementText(attributed),
      href: attributed instanceof HTMLAnchorElement ? attributed.getAttribute("href") : null,
    });
  } catch (error) {
    devError("❌ autocapture click handler failed:", error);
  }
}

/** Attach the global click listener once. No-op on the server. */
export function initAutocapture(): void {
  if (initialized || typeof document === "undefined") return;
  initialized = true;
  document.addEventListener("click", onClick, { capture: true, passive: true });
}
